import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ChevronDown, ArrowRight } from 'lucide-react';

const FAQ_DATA = {
  es: [
    { question: '¿Es seguro invertir en Tulum?', answer: 'Sí. La clave está en verificar que el proyecto tenga MIA vigente, fideicomiso en regla y desarrollador con historial comprobado. En Marea Riviera filtramos cada proyecto antes de mostrártelo.' },
    { question: '¿Cuánto necesito para invertir en Riviera Maya?', answer: 'Desde $90,000 USD en Puerto Morelos hasta $500,000 USD en Tulum. Tenemos opciones para diferentes perfiles de inversionista con rendimiento proyectado de 8-15% anual.' },
    { question: '¿Qué es una MIA y por qué es importante?', answer: 'La Manifestación de Impacto Ambiental es el permiso ambiental del gobierno mexicano. Sin ella, un proyecto puede ser clausurado. En Marea Riviera solo trabajamos con proyectos que la tienen vigente.' },
    { question: '¿Pueden invertir extranjeros en México?', answer: 'Sí. A través de un fideicomiso bancario, los extranjeros pueden comprar propiedades en zona costera con todos los derechos de uso, renta y venta.' },
    { question: '¿Cuánto genera un departamento en Tulum en Airbnb?', answer: 'Un departamento de 2 recámaras bien gestionado genera entre $1,500 y $2,500 USD mensuales con una ocupación promedio del 78%. El property management está incluido en todos nuestros proyectos.' },
    { question: '¿Cómo es el proceso de compra con Marea Riviera?', answer: '5 pasos: Consulta gratuita → Selección de propiedad → Due diligence legal → Escrituración → Property management activo desde día 1.' }
  ],
  en: [
    { question: 'Is it safe to invest in Tulum?', answer: 'Yes. The key is verifying that the project has a current MIA, a proper trust, and a developer with a proven track record. At Marea Riviera, we filter every project before showing it to you.' },
    { question: 'How much do I need to invest in Riviera Maya?', answer: 'From $90,000 USD in Puerto Morelos to $500,000 USD in Tulum. We have options for different investor profiles with a projected yield of 8-15% annually.' },
    { question: 'What is a MIA and why is it important?', answer: 'The Environmental Impact Statement (MIA) is the Mexican government\'s environmental permit. Without it, a project can be shut down. At Marea Riviera, we only work with projects that have a current one.' },
    { question: 'Can foreigners invest in Mexico?', answer: 'Yes. Through a bank trust (fideicomiso), foreigners can purchase properties in coastal areas with full rights of use, rental, and sale.' },
    { question: 'How much does an apartment in Tulum generate on Airbnb?', answer: 'A well-managed 2-bedroom apartment generates between $1,500 and $2,500 USD monthly with an average occupancy of 78%. Property management is included in all our projects.' },
    { question: 'What is the buying process with Marea Riviera?', answer: '5 steps: Free consultation → Property selection → Legal due diligence → Title deed → Active property management from day 1.' }
  ]
};

const WHATSAPP = "https://wa.me/529841585732?text=Hola%2C%20quiero%20agendar%20una%20visita%20privada";
const FAQ_IMAGE = "https://customer-assets.emergentagent.com/job_tulum-investment-lab/artifacts/5alho77m_magnific_ultra-slow-architectural-_xghAxftjfW.mp4";

export const FAQ = () => {
  const { language } = useLanguage();
  const [openIndex, setOpenIndex] = useState(0);
  const faqs = FAQ_DATA[language] || FAQ_DATA.es;

  return (
    <section id="faq" data-testid="faq-section" className="bg-[#FAFAF7] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-16 lg:px-24">
        <div className="text-center mb-16">
          <span className="inline-block font-inter text-xs uppercase tracking-[0.25em] text-[#0A2235]/60 border border-[#0A2235]/15 px-4 py-1.5 rounded-sm mb-8">
            FAQ
          </span>
          <h2 className="font-playfair text-[clamp(2rem,5vw,4rem)] text-[#0A2235] leading-[1.08] tracking-tight">
            {language === 'es' ? (
              <>Tu inversión, guiada con <em className="text-[#2BBFBF]">claridad</em></>
            ) : (
              <>Your investment, <em className="text-[#2BBFBF]">thoughtfully</em> guided</>
            )}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Left: CTA + Image - Aurven style */}
          <div className="lg:col-span-2 space-y-6">
            <div className="border border-[#0A2235]/10 rounded-2xl p-8">
              <h3 className="font-playfair text-xl text-[#0A2235] mb-3">
                {language === 'es' ? 'Agenda una visita privada' : 'Schedule a private viewing'}
              </h3>
              <p className="font-inter text-[#0A2235]/50 text-sm mb-6 leading-relaxed">
                {language === 'es' ? 'Consulta personalizada en el horario que prefieras.' : 'Personalized consultation at your preferred time.'}
              </p>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="inline-flex items-center group">
                <span className="font-inter text-[#0A2235] text-sm tracking-wider border border-[#0A2235]/20 px-5 py-2.5 rounded-l-full group-hover:bg-[#0A2235] group-hover:text-[#FAFAF7] transition-colors">
                  {language === 'es' ? 'Agendar ahora' : 'Enquire now'}
                </span>
                <span className="bg-[#0A2235] p-2.5 rounded-r-full group-hover:bg-[#2BBFBF] transition-colors">
                  <ArrowRight className="w-4 h-4 text-[#FAFAF7]" />
                </span>
              </a>
            </div>
            <video src={FAQ_IMAGE} className="w-full h-64 object-cover rounded-2xl" autoPlay muted loop playsInline preload="metadata" />
          </div>

          {/* Right: Accordion */}
          <div className="lg:col-span-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-[#0A2235]/10 last:border-b-0">
                <button onClick={() => setOpenIndex(openIndex === index ? -1 : index)} className="w-full flex items-center justify-between py-6 text-left group" data-testid="faq-toggle">
                  <h3 className="font-playfair text-lg text-[#0A2235] pr-4 group-hover:text-[#2BBFBF] transition-colors">{faq.question}</h3>
                  <ChevronDown className={`w-5 h-5 text-[#2BBFBF] flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96 pb-6' : 'max-h-0'}`}>
                  <p className="font-inter text-[#0A2235]/60 leading-[1.7] text-sm">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
